// Dispatches register (BUILD_SPEC.md §11 "Registers with fast fuzzy search +
// filters"). Plan.md G23.
// Columns come from what GET /sales/dispatches REALLY returns — raw rows plus
// the endpoint's own `lineCount`/`totalMarginCents` aggregates (see api.ts's
// `DispatchListRow`; 2026-07-10 wave-gate BLOCKER: this file computed the
// margin over `dispatch.lines`, which the list path never returned, crashing
// the register on any real row — same root cause as SalesOrdersRegister's
// fix). Line detail lives on the detail page (the serialized by-id shape).
import { useState } from 'react';
import { Button, Group, Select, Stack, Table } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { IconPlus, IconTruckOff } from '@tabler/icons-react';
import { DISPATCH_STATUSES, type DispatchStatus } from '@zebra/core';
import { formatMoneyCents, formatDateTime } from './format';
import { useDispatchesList } from './api';
import { DocStatusBadge } from '../../components/DocStatusBadge';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { CustomerName } from './components/EntityNames';

const NEW_DISPATCH_BUTTONS = (
  <>
    <Button component={Link} to="/sales-invoicing" leftSection={<IconPlus size={16} />}>
      Dispatch a load
    </Button>
    <Button component={Link} to="/sales-invoicing/dispatch-manual" variant="subtle">
      Dispatch by count (no weighbridge)
    </Button>
  </>
);

export function DispatchesRegister() {
  const [status, setStatus] = useState<DispatchStatus | undefined>(undefined);
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const cursor = cursors[cursors.length - 1];
  const { data, isLoading } = useDispatchesList({ cursor, status });
  const rows = data?.items ?? [];
  // Header/EmptyState CTA de-dupe (guidance-wave gap, 2026-08-26; found by
  // audit alongside SalesOrdersRegister's identical issue): on an empty
  // register the EmptyState below already offers NEW_DISPATCH_BUTTONS as the
  // sole call-to-action — showing them in the page header too rendered
  // duplicate buttons on a fresh tenant. Reuses the exact emptiness condition
  // the EmptyState branch checks.
  const showHeaderCreate = isLoading || rows.length > 0;

  return (
    <Stack gap="md">
      <PageHeader
        title="Dispatches"
        description="Every load that's left the yard, weighbridge or manual — the step between a sales order and an invoice in sales order → dispatch → invoice → credit note."
        actions={
          <Group gap="sm">
            <Select
              placeholder="All statuses"
              clearable
              data={DISPATCH_STATUSES.map((s) => ({ value: s, label: s }))}
              value={status ?? null}
              onChange={(v) => {
                setStatus((v as DispatchStatus) ?? undefined);
                setCursors([undefined]);
              }}
              w={160}
            />
            {showHeaderCreate && NEW_DISPATCH_BUTTONS}
          </Group>
        }
      />

      <Table.ScrollContainer minWidth={640}>
        <Table striped highlightOnHover verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Number</Table.Th>
              <Table.Th>Customer</Table.Th>
              <Table.Th>Dispatched</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Lines</Table.Th>
              <Table.Th ta="right">Margin</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((dispatch) => (
              <Table.Tr key={dispatch.id}>
                <Table.Td>
                  <Link to="/sales-invoicing/dispatches/$dispatchId" params={{ dispatchId: dispatch.id }}>
                    {dispatch.number}
                  </Link>
                </Table.Td>
                <Table.Td>
                  <CustomerName id={dispatch.customerId} />
                </Table.Td>
                <Table.Td>{formatDateTime(dispatch.dispatchedAt)}</Table.Td>
                <Table.Td>
                  <DocStatusBadge status={dispatch.status} />
                </Table.Td>
                <Table.Td>{dispatch.lineCount}</Table.Td>
                {/* Server-computed, null-propagating total (null until POSTED
                    prices every line) — see DispatchListRow's doc comment. */}
                <Table.Td ta="right">{dispatch.totalMarginCents !== null ? formatMoneyCents(dispatch.totalMarginCents) : '—'}</Table.Td>
              </Table.Tr>
            ))}
            {!isLoading && rows.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <EmptyState
                    icon={IconTruckOff}
                    title="No dispatches yet"
                    hint="Post a load over the weighbridge, or dispatch counted goods without one, to see it here."
                    action={NEW_DISPATCH_BUTTONS}
                  />
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {data?.nextCursor && (
        <Group justify="center">
          <Button variant="subtle" onClick={() => setCursors((prev) => [...prev, data.nextCursor ?? undefined])}>
            Load more
          </Button>
        </Group>
      )}
    </Stack>
  );
}
